<template>
    <b-card class="rounded-lg">
        <Loading v-if="loadingState" />

        <template v-if="duplicatesDetails">
            <!-- Error message display when data couldn't be fetched -->
            <b-alert v-if="hasDataErrors" show variant="warning" class="mb-3">
                <h5 class="alert-heading">
                    <feather-icon icon="AlertTriangleIcon" class="mr-1" />
                    {{ $t('Message.partialDataWarning.title') }}
                </h5>
                <p class="mb-2">{{ $t('Message.partialDataWarning.message') }}</p>
                <hr>
                <div v-if="originalError" class="mb-2">
                    <strong>{{ $t('Duplicates.originalInvoice') }}:</strong> {{ originalError }}
                </div>
                <div v-if="duplicateErrors.length > 0">
                    <strong>{{ $t('Duplicates.duplicateInvoices') }}:</strong>
                    <ul class="mb-0 mt-1">
                        <li v-for="(error, index) in duplicateErrors" :key="index">{{ error }}</li>
                    </ul>
                </div>
                <p class="mb-0 mt-2">
                    <small>{{ $t('Message.partialDataWarning.suggestion') }}</small>
                </p>
            </b-alert>

            <!-- Only show legend and content if we have valid data -->
            <template v-if="hasValidData">
                <b-row class="mt-2 d-block d-sm-block d-md-none">
                    <b-col
                        v-for="(details, index) in detailsLegend"
                        :key="index"
                        cols="12"
                        sm="6"
                        class="d-flex mb-1"
                        style="gap: 0.5rem"
                    >
                        <div
                            class="bulletIcon my-auto"
                            :class="details.variantColor"
                        />
                        <p class="mb-0" v-text="$t(details.text)" />
                    </b-col>
                </b-row>

                <div class="d-none d-md-flex" style="gap: 1.5rem">
                    <div
                        v-for="(details, index) in detailsLegend"
                        :key="index"
                        class="d-flex mb-1"
                        style="gap: 0.5rem"
                    >
                        <div
                            class="bulletIcon my-auto"
                            :class="details.variantColor"
                        />
                        <p class="mb-0" v-text="$t(details.text)" />
                    </div>
                </div>

                <b-row class="mt-2">
                    <DuplicatesDetailsOriginal
                        v-if="!originalError"
                        :locale="locale"
                        :original="original"
                        :resource="resource"
                        :matchedFields="matchedFields"
                        :comparedFields="comparedFields"
                        :notComparedFields="notComparedFields"
                    />

                    <b-col cols="12" lg="6" v-if="validDuplicates.length > 0">
                        <b-row>
                            <b-col cols="12">
                                <DuplicateDetailsDuplicate
                                    :locale="locale"
                                    :resource="resource"
                                    :duplicate="
                                        validDuplicates[pagination.currentPage - 1]
                                    "
                                    :matchedFields="matchedFields"
                                    :comparedFields="comparedFields"
                                    :notComparedFields="notComparedFields"
                                    @chooseDuplicate="openAction($event)"
                                />
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </template>
        </template>

            <b-row align-h="between" class="mt-2" v-if="hasValidData">
                <b-col
                    cols="12"
                    sm="6"
                    md="6"
                    class="d-flex align-items-center"
                >
                    <v-select
                        v-if="selectAction"
                        v-model="todoSelected"
                        @input="filterTodo($event)"
                        :clearable="false"
                        :placeholder="$t(locale.todo)"
                        :options="$t(locale.todoOptions)"
                        class="selected d-inline-block"
                    />
                </b-col>

                <b-col
                    cols="12"
                    md="6"
                    sm="6"
                    class="d-flex justify-content-center justify-content-sm-end py-1"
                >
                    <b-pagination
                        v-model="pagination.currentPage"
                        :total-rows="validDuplicates.length"
                        :per-page="pagination.perPage"
                        first-number
                        last-number
                        prev-class="prev-item"
                        next-class="next-item"
                    >
                        <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </b-col>
            </b-row>

            <ModalSingleHandle
                v-if="validDuplicates.length > 0"
                :uuid="$route.params.id"
                :document_num="chooseDocumentNum"
                :title="singleHandleTitle"
                @refresh="clearTodo"
            />
    </b-card>
</template>

<script>
import DuplicatesDetailsOriginal from '@/@core/Duplicates/DuplicatesDetailsOriginal.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useAppConfig from '@core/app-config/useAppConfig';
import vSelect from 'vue-select';
import ModalSingleHandle from '@/@core/Duplicates/ModalSingleHandle.vue';
import DuplicateDetailsDuplicate from '@/@core/Duplicates/DuplicateDetailsDuplicate.vue';

export default {
    name: 'DuplicatesDetails',
    components: {
        vSelect,
        DuplicatesDetailsOriginal,
        ModalSingleHandle,
        DuplicateDetailsDuplicate,
    },
    data() {
        return {
            todoSelected: '',
            config: useAppConfig(),
            original: {},
            resource: '',
            chooseDuplicate: '',
            singleHandleTitle: '',
            duplicates: '',
            loadingState: true,
            duplicatesDetails: false,
            selectAction: false,
            matchedFields: [],
            comparedFields: [],
            notComparedFields: [],
            chooseDocumentNum: null,
            originalError: null,
            duplicateErrors: [],
            detailsLegend: [
                {
                    variantColor: 'bg-blueLight',
                    text: 'Duplicates.compareAndMatched',
                },
                {
                    variantColor: 'bg-warning',
                    text: 'Duplicates.notMatched',
                },
                {
                    variantColor: 'bg-danger',
                    text: 'Duplicates.compareAndNotMatched',
                },
            ],
            locale: {
                duplicatesDetail: 'Duplicates.duplicatesDetail',
                field: 'Duplicates.field',
                value: 'Duplicates.value',
                todo: 'Duplicates.todo',
                todoOptions: 'Duplicates.todoOptionsDetails',
                accidentmsg: 'Duplicates.accidentMsg',
                awatingDuplicateFetch: 'Duplicates.awatingDuplicateFetch',
            },
            pagination: {
                currentPage: 1,
                perPage: 1,
            },
        };
    },

    computed: {
        msg() {
            if (this.getLang == '') {
                return this.swedishLang ? 'message' : 'eng_message';
            } else return this.getLang == 'sv' ? 'message' : 'eng_message';
        },
        getLang() {
            return this.config.lang.value;
        },
        hasDataErrors() {
            return this.originalError || this.duplicateErrors.length > 0;
        },
        hasValidData() {
            return !this.originalError || this.validDuplicates.length > 0;
        },
        validDuplicates() {
            if (!Array.isArray(this.duplicates)) {
                return [];
            }
            return this.duplicates.filter(duplicate => 
                typeof duplicate === 'object' && 
                duplicate !== null && 
                !duplicate.toString().includes('not fetched') &&
                duplicate.document_num
            );
        }
    },

    methods: {
        filterTodo(value) {
            if (value.event !== '') {
                if (value.event == 'notDuplicated') {
                    this.todoSelected = '';
                    this.$swal({
                        text: this.$t(this.locale.accidentmsg, {
                            originalTitle: this.chooseDuplicate,
                        }),
                        icon: 'warning',
                        showCancelButton: true,
                        reverseButtons: true,
                        denyButtonText: this.$t('UsersCard.cancel'),
                        confirmButtonText: this.$t('Duplicates.confirm'),
                        customClass: {
                            confirmButton: 'btn btn-primary',
                            cancelButton: 'btn btn-danger mr-1',
                        },
                        buttonsStyling: false,
                    }).then(res => {
                        if (res.value) {
                            this.$useJwt
                                .notDuplicate(
                                    this.$route.params.id,
                                    this.validDuplicates[
                                        this.pagination.currentPage - 1
                                    ].document_num
                                )
                                .then(res => {
                                    this.todoSelected = '';
                                    this.popupMsg(
                                        this.$t('Message.Success'),
                                        res.data[this.msg],
                                        'CheckIcon',
                                        'success'
                                    );
                                });
                        }
                    });
                } else {
                    this.singleHandleTitle = `${
                        this.resource.charAt(0).toUpperCase() +
                        this.resource.slice(1)
                    } ${
                        this.validDuplicates[this.pagination.currentPage - 1]
                            .document_num
                    }`;

                    this.chooseDocumentNum =
                        this.validDuplicates[
                            this.pagination.currentPage - 1
                        ].document_num;

                    this.$bvModal.show('modalSingleHandle');
                }
            }
        },

        clearTodo($event) {
            this.todoSelected = '';
            this.chooseDocumentNum = null;

            if ($event !== 'close') {
                this.getDetailsDuplicates(this.$route.params.id, false);
            }
        },

        openAction(value) {
            this.chooseDuplicate = value.data;
            this.selectAction = value.action;
        },
        generateColorByValue(
            matched_fields,
            compared_fields,
            not_compared_fields
        ) {
            matched_fields.forEach(element => {
                this.matchedFields.push({
                    text: element,
                    colorClass: 'text-blueLight',
                });
            });

            compared_fields.forEach(element => {
                this.comparedFields.push({
                    text: element,
                    colorClass: 'text-danger',
                });
            });

            not_compared_fields.forEach(element => {
                this.notComparedFields.push({
                    text: element,
                    colorClass: 'text-warning',
                });
            });
        },
        getDetailsDuplicates(uuid, firstInstall) {
            this.loadingState = true;
            this.duplicatesDetails = false;
            // Clear previous errors
            this.originalError = null;
            this.duplicateErrors = [];

            if (firstInstall) {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: this.$t('Message.wait'),
                        variant: 'warning',
                        text: this.$t('Duplicates.awatingDuplicateFetch'),
                        waiting: true,
                    },
                });
            }

            this.$useJwt.getDuplicateDetails(uuid).then(res => {
                this.duplicates = [];
                this.$toast.clear();
                const {data} = res.data;
                const {Original} = data;
                const {Duplicates} = data;
                const {Resource} = data;
                const {matched_fields} = data; //Green color
                const {compared_fields} = data; //Red Color
                const {not_compared_fields} = data; //Orange Color

                // Check if Original is an error message
                if (typeof Original === 'string' && Original.includes('could not be fetched')) {
                    this.originalError = Original;
                    this.original = {}; // Set empty object to prevent errors
                } else {
                    this.original = Original;
                }

                // Check for duplicate errors and filter out error messages
                if (Array.isArray(Duplicates)) {
                    this.duplicateErrors = [];
                    const validDuplicates = [];
                    
                    Duplicates.forEach(duplicate => {
                        if (typeof duplicate === 'string' && duplicate.includes('not fetched')) {
                            this.duplicateErrors.push(duplicate);
                        } else if (typeof duplicate === 'object' && duplicate !== null) {
                            duplicate.active = false;
                            validDuplicates.push(duplicate);
                        }
                    });
                    
                    this.duplicates = validDuplicates;
                } else {
                    this.duplicates = [];
                }

                //Generate color coding for fields
                this.generateColorByValue(
                    matched_fields,
                    compared_fields,
                    not_compared_fields
                );

                this.resource = Resource;

                this.loadingState = false;
                this.duplicatesDetails = true;
            });
        },
    },
    mounted() {
        this.getDetailsDuplicates(this.$route.params.id, true);
    },
};
</script>

<style scoped lang="scss">
.selected {
    width: 100%;
}
.bulletIcon {
    width: 15px;
    height: 15px;
    border-radius: 50%;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
